import React from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { useWizard } from "../../../../shared/components/common/ui/Wizard/useWizard";
import { CAMPAIGN_WIZARD_STEPS } from "../steps/campaignWizardSteps";
import { WizardStepper } from "../../../../shared/components/common/ui/Wizard/WizardStepper";
import { WizardFooter } from "../../../../shared/components/common/ui/Wizard/WizardFooter";
import { buildCampaignPayload } from "./buildCampaignPayload";
import {
  getEmptyCampaignWizardData,
  mapCampaignToWizardData,
} from "./campaignWizardMappers";

import { CampaignSave } from "../../../../services/api/Campaigns/CampaignSave";
import { CampaignUpdate } from "../../../../services/api/Campaigns/CampaignUpdate";
import { useSheets } from "../../hooks/useSheets";
import { setStoredCampaignCategory } from "../../utils/campaignCategoryStorage";

export function CampaignWizard({ mode = "create", campaign = null }) {
  const wizard = useWizard({
    steps: CAMPAIGN_WIZARD_STEPS,
    initialStepId: "basic",
  });

  const navigate = useNavigate();
  const { rows: sheets } = useSheets();

  const [campaignData, setCampaignData] = React.useState(() =>
    mode === "edit" && campaign
      ? mapCampaignToWizardData(campaign)
      : getEmptyCampaignWizardData()
  );

  const [isStepValid, setIsStepValid] = React.useState(false);
  const [attemptedNext, setAttemptedNext] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (mode !== "edit") {
      setCampaignData(getEmptyCampaignWizardData());
      return;
    }

    setCampaignData(mapCampaignToWizardData(campaign));
  }, [campaign, mode]);

  const updateSection = (sectionId, values) => {
    setCampaignData((prev) => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        ...values,
      },
    }));
  };

  const CurrentStepComponent = wizard.currentStep.component;

  React.useEffect(() => {
    setAttemptedNext(false);
  }, [wizard.currentStep.id]);

  React.useEffect(() => {
    if (!campaignData.audience?.audience_id || campaignData.audience?.audience) {
      return;
    }

    const selectedAudience = sheets.find(
      (item) => String(item.id) === String(campaignData.audience.audience_id)
    );

    if (!selectedAudience) return;

    setCampaignData((prev) => ({
      ...prev,
      audience: {
        ...prev.audience,
        audience: selectedAudience,
      },
    }));
  }, [campaignData.audience?.audience, campaignData.audience?.audience_id, sheets]);

  const handleSubmit = async () => {
    try {
      setIsSaving(true);

      const payload = buildCampaignPayload(campaignData, { mode, campaign });

      const response =
        mode === "edit"
          ? await CampaignUpdate(payload)
          : await CampaignSave(payload);

      if (response.message === "success") {
        const campaignId =
          mode === "edit"
            ? campaign?.id ?? campaign?.code
            : response.code;

        setStoredCampaignCategory(campaignId, campaignData.basic?.category);

        navigate("/campanhas", {
          state: { shouldRefetch: true },
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = () => {
    if (isSaving) return;

    if (wizard.isLastStep) {
      if (!isStepValid) {
        setAttemptedNext(true);
        return;
      }

      handleSubmit();
      return;
    }

    if (!isStepValid) {
      setAttemptedNext(true);
      return;
    }

    setAttemptedNext(false);
    wizard.next();
  };

  const handleBack = () => {
    if (isSaving) return;
    setAttemptedNext(false);
    wizard.back();
  };

  React.useEffect(() => {
    const isSingle = campaignData.schedule?.type === "single";
    const isBusinessDays = campaignData.sendRules?.businessDaysOnly;

    if (isSingle && isBusinessDays) {
      setCampaignData((prev) => ({
        ...prev,
        sendRules: {
          ...prev.sendRules,
          businessDaysOnly: false,
        },
      }));
    }
  }, [campaignData.schedule?.type, campaignData.sendRules?.businessDaysOnly]);

  return (
    <Box sx={{ width: "100%", px: { xs: 2, sm: 4 }, py: { xs: 2, sm: 3 } }}>
      <WizardStepper
        steps={wizard.steps}
        activeStep={wizard.currentStepIndex}
      />

      <Box sx={{ maxWidth: 860, width: "100%" }}>
        <CurrentStepComponent
          data={campaignData[wizard.currentStep.id]}
          updateData={(values) => updateSection(wizard.currentStep.id, values)}
          setIsValid={setIsStepValid}
          showErrors={attemptedNext}
          mode={mode}
          campaign={campaign}
          campaignType={campaignData.schedule?.type}
          audienceId={campaignData.audience?.audience_id}
        />
      </Box>

      <WizardFooter
        canGoBack={wizard.canGoBack}
        onBack={handleBack}
        onNext={handleNext}
        nextLabel={
          wizard.isLastStep
            ? mode === "edit"
              ? "Salvar alterações"
              : "Finalizar"
            : "Avançar"
        }
        isSaving={isSaving}
      />
    </Box>
  );
}
